// Shared SEO engine for ReFound marketing site.
// Centralises the imperative <head> mutations and JSON-LD builders that were
// previously duplicated across HomeSEO / BlogScanSEO, so every content page and
// article gets consistent metadata, canonical/hreflang links and schema markup.

import type { ReceiptExample, Section } from "@/data/content-types";

export const SITE_URL = "https://refoundapp.ca";
export const OG_DEFAULT = `${SITE_URL}/og-base.png`;
export const OG_DEFAULT_EN = `${SITE_URL}/og-base-en.png`;

/** Default share image in the page language (FR artwork is the global default). */
export function ogDefaultFor(lang: "fr" | "en") {
  return lang === "en" ? OG_DEFAULT_EN : OG_DEFAULT;
}
export const LOGO_URL = `${SITE_URL}/icon.png`;

export const APP_STORE_URL =
  "https://apps.apple.com/ca/app/refound-receipts-returns/id6756928133";
export const APP_STORE_URL_FR = `${APP_STORE_URL}?l=fr-CA`;

/** Google Play listing (Android). Published October 2026. */
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.vibecode.smartreceiptmanager";
export const PLAY_STORE_URL_FR = `${PLAY_STORE_URL}&hl=fr-CA`;
export const PLAY_STORE_URL_EN = `${PLAY_STORE_URL}&hl=en-CA`;
export const ANDROID_PACKAGE = "com.vibecode.smartreceiptmanager";
export const IOS_APP_ID = "6756928133";

export type Lang = "fr" | "en";

export function htmlLangOf(lang: Lang) {
  return lang === "fr" ? "fr-CA" : "en-CA";
}
export function ogLocaleOf(lang: Lang) {
  return lang === "fr" ? "fr_CA" : "en_CA";
}

/* ------------------------------------------------------------------ */
/* Low-level <head> upserts                                            */
/* ------------------------------------------------------------------ */

export function upsertMeta(
  attr: "name" | "property",
  value: string,
  content: string,
) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function upsertJsonLd(id: string, data: object) {
  const selector = `script[data-seo-id="${id}"]`;
  let el = document.head.querySelector<HTMLScriptElement>(selector);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/** Drop a JSON-LD block (used when navigating to a page that has none). */
export function removeJsonLd(id: string) {
  document.head.querySelector(`script[data-seo-id="${id}"]`)?.remove();
}

/* ------------------------------------------------------------------ */
/* High-level page metadata                                            */
/* ------------------------------------------------------------------ */

export interface PageSeoConfig {
  lang: Lang;
  title: string;
  description: string;
  keywords?: string;
  /** Absolute path of the current-language page, e.g. "/application-recus" */
  path: string;
  /** Absolute path of the FR variant (for hreflang) */
  frPath: string;
  /** Absolute path of the EN variant (for hreflang) */
  enPath: string;
  ogImage?: string;
  ogType?: "website" | "article";
}

/** Apply title, description, OG/Twitter, canonical and hreflang for a page. */
export function applyPageSeo(cfg: PageSeoConfig) {
  const lang = cfg.lang;
  const htmlLang = htmlLangOf(lang);
  const url = `${SITE_URL}${cfg.path}`;
  const urlFr = `${SITE_URL}${cfg.frPath}`;
  const urlEn = `${SITE_URL}${cfg.enPath}`;
  const ogImage = cfg.ogImage || ogDefaultFor(cfg.lang);

  document.title = cfg.title;
  document.documentElement.setAttribute("lang", htmlLang);

  upsertMeta("name", "description", cfg.description);
  if (cfg.keywords) upsertMeta("name", "keywords", cfg.keywords);
  upsertMeta("name", "author", "ReFound");
  upsertMeta(
    "name",
    "robots",
    "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  );
  upsertMeta("name", "googlebot", "index, follow");
  upsertMeta("name", "bingbot", "index, follow");

  // Open Graph
  upsertMeta("property", "og:type", cfg.ogType || "website");
  upsertMeta("property", "og:title", cfg.title);
  upsertMeta("property", "og:description", cfg.description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", ogImage);
  upsertMeta("property", "og:image:alt", "ReFound");
  upsertMeta("property", "og:locale", ogLocaleOf(lang));
  upsertMeta("property", "og:locale:alternate", ogLocaleOf(lang === "fr" ? "en" : "fr"));
  upsertMeta("property", "og:site_name", "ReFound");

  // Twitter
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", cfg.title);
  upsertMeta("name", "twitter:description", cfg.description);
  upsertMeta("name", "twitter:image", ogImage);

  // Canonical + hreflang
  upsertLink("canonical", url);
  upsertLink("alternate", urlFr, "fr-CA");
  upsertLink("alternate", urlEn, "en-CA");
  upsertLink("alternate", urlFr, "x-default");
}

/* ------------------------------------------------------------------ */
/* JSON-LD builders                                                    */
/* ------------------------------------------------------------------ */

export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}#organization`,
    name: "ReFound",
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: LOGO_URL },
    email: "info@refoundapp.ca",
    areaServed: "CA",
    sameAs: [],
  };
}

/** MobileApplication / SoftwareApplication schema for ReFound. */
export function mobileAppLd(lang: Lang, description: string) {
  const isFr = lang === "fr";
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "@id": `${SITE_URL}#app`,
    name: "ReFound",
    operatingSystem: "iOS, Android",
    applicationCategory: "ShoppingApplication",
    applicationSubCategory: "Receipt management",
    url: SITE_URL,
    image: OG_DEFAULT,
    description,
    countriesSupported: "CA",
    inLanguage: ["fr-CA", "en-CA"],
    offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" },
    author: { "@id": `${SITE_URL}#organization` },
    publisher: { "@id": `${SITE_URL}#organization` },
    downloadUrl: [APP_STORE_URL, PLAY_STORE_URL],
    installUrl: [APP_STORE_URL, PLAY_STORE_URL],
    sameAs: [APP_STORE_URL, PLAY_STORE_URL],
    featureList: isFr
      ? [
          "Numérisation de reçus",
          "Reçus enrichis (nom et image du produit, catégorie)",
          "Recherche rapide de reçus",
          "Aide aux retours en magasin",
          "Affichage du code-barres du reçu quand disponible",
          "Suivi des garanties et dates limites de retour estimées",
          "Partage de reçus avec un conjoint ou un membre de la famille",
          "Organisation des preuves d'achat",
        ]
      : [
          "Receipt scanning",
          "Enriched receipts (product name and image, category)",
          "Fast receipt search",
          "In-store return assistance",
          "Receipt barcode display when available",
          "Warranty tracking and estimated return deadlines",
          "Family receipt sharing with a partner or household member",
          "Proof of purchase organization",
        ],
  };
}

export interface FaqItem {
  q: string;
  a: string;
}

export function faqLd(items: readonly FaqItem[], id: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": id,
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export interface Crumb {
  name: string;
  url?: string;
}

export function breadcrumbLd(crumbs: readonly Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      ...(c.url ? { item: c.url } : {}),
    })),
  };
}

export function articleLd(opts: {
  lang: Lang;
  url: string;
  headline: string;
  description: string;
  image: string;
  section: string;
  datePublished: string;
  dateModified: string;
  keywords?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${opts.url}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": opts.url },
    headline: opts.headline,
    description: opts.description,
    inLanguage: htmlLangOf(opts.lang),
    datePublished: opts.datePublished,
    dateModified: opts.dateModified,
    image: [opts.image],
    author: { "@type": "Organization", name: "ReFound", url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: "ReFound",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    articleSection: opts.section,
    ...(opts.keywords ? { keywords: opts.keywords } : {}),
  };
}

export function webPageLd(opts: {
  lang: Lang;
  url: string;
  title: string;
  description: string;
  ogImage?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${opts.url}#webpage`,
    url: opts.url,
    name: opts.title,
    description: opts.description,
    inLanguage: htmlLangOf(opts.lang),
    isPartOf: { "@id": `${SITE_URL}#website` },
    about: { "@id": `${SITE_URL}#app` },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: opts.ogImage || OG_DEFAULT,
    },
  };
}

export function appStoreUrlFor(lang: Lang) {
  return lang === "fr" ? APP_STORE_URL_FR : APP_STORE_URL;
}

export function playStoreUrlFor(lang: Lang) {
  return lang === "fr" ? PLAY_STORE_URL_FR : PLAY_STORE_URL_EN;
}

/** Both store listings, for JSON-LD `downloadUrl` / `installUrl`. */
export const STORE_URLS = [APP_STORE_URL, PLAY_STORE_URL] as const;

/* ------------------------------------------------------------------ */
/* Receipt-line -> product mappings (merchant pages)                   */
/* ------------------------------------------------------------------ */

/** Flatten every ReceiptExample of a page, sections then sub-sections. */
export function collectReceiptExamples(sections: readonly Section[]): ReceiptExample[] {
  const out: ReceiptExample[] = [];
  for (const s of sections) {
    if (s.examples) out.push(...s.examples);
    for (const sub of s.sub ?? []) {
      if (sub.examples) out.push(...sub.examples);
    }
  }
  return out;
}

/**
 * ItemList mirroring, one-for-one, the "on the receipt -> in ReFound" blocks
 * that are visible on the page. Nothing is added here that a reader cannot see
 * in the HTML text; it exists only to make the mapping machine-readable.
 * Returns null when the page has no examples.
 */
export function receiptExamplesLd(opts: {
  lang: Lang;
  url: string;
  merchant: string;
  sections: readonly Section[];
}) {
  const examples = collectReceiptExamples(opts.sections);
  if (!examples.length) return null;
  const isFr = opts.lang === "fr";
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${opts.url}#receipt-examples`,
    url: opts.url,
    inLanguage: htmlLangOf(opts.lang),
    isPartOf: { "@id": `${opts.url}#webpage` },
    name: isFr
      ? `Exemples de lignes de reçu ${opts.merchant} et produits identifiés par ReFound`
      : `${opts.merchant} receipt lines and the products ReFound identified`,
    description: isFr
      ? `Lignes provenant d'un reçu ${opts.merchant} numérisé dans un compte de démonstration ReFound, et produit identifié pour chacune.`
      : `Lines from a ${opts.merchant} receipt scanned into a ReFound demo account, and the product identified for each one.`,
    numberOfItems: examples.length,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: examples.map((ex, i) => {
      const printed = ex.code ? `${ex.code} — ${ex.receiptLine}` : ex.receiptLine;
      const parts = [
        isFr
          ? `Sur notre reçu de démonstration ${opts.merchant}, la ligne « ${ex.receiptLine} »${ex.code ? ` (code ${ex.code})` : ""} est identifiée par ReFound comme : ${ex.productName}.`
          : `On our demo ${opts.merchant} receipt, the line "${ex.receiptLine}"${ex.code ? ` (code ${ex.code})` : ""} is identified by ReFound as: ${ex.productName}.`,
        ...(ex.details ?? []),
        ...(ex.warranty ? [ex.warranty] : []),
      ];
      return {
        "@type": "ListItem",
        position: i + 1,
        name: printed,
        description: parts.join(" "),
        // Only mirrors what the block already shows: the product photo ReFound
        // attached, and the merchant product page it linked to.
        ...(ex.image || ex.merchantUrl
          ? {
              item: {
                "@type": "Product",
                name: ex.productName,
                ...(ex.image
                  ? {
                      image: ex.image.src.startsWith("http")
                        ? ex.image.src
                        : `${SITE_URL}${ex.image.src}`,
                    }
                  : {}),
                ...(ex.merchantUrl ? { url: ex.merchantUrl } : {}),
              },
            }
          : {}),
      };
    }),
  };
}
